import { Injectable, inject } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment, UserService, SubscriptionService } from '@lumio/shared';
import { LumioIAP } from '../plugins/lumio-iap.plugin';

/** Result of a purchase attempt via App Store. */
export type IapPurchaseResult =
  | 'success'
  | 'cancelled'
  | 'pending'                   // awaiting parental approval
  | 'already_subscribed_stripe' // 409 BILLING_ALREADY_SUBSCRIBED_VIA_OTHER_PROVIDER
  | 'already_subscribed_apple'  // 409 BILLING_SUBSCRIPTION_ALREADY_EXISTS
  | 'linked_to_other_account'   // 409 BILLING_APPLE_TXN_LINKED_TO_ANOTHER_ACCOUNT
  | 'subscription_not_active'   // 422 BILLING_APPLE_SUBSCRIPTION_NOT_ACTIVE
  | 'service_unavailable'       // 503 — purchase kept pending for retry
  | 'error';

/** Result of a restore attempt. */
export type IapRestoreResult =
  | 'success'
  | 'not_found'      // 404 — no prior Apple purchase (benign)
  | 'wrong_provider' // 409 — has an active Stripe subscription
  | 'unavailable'    // 503 — Apple servers temporarily down
  | 'error';

const PENDING_TXN_KEY = 'iap.pending_transaction_id';
const TAG = '[IapService]';

function log(...args: unknown[]): void {
  console.log(TAG, ...args);
}

function readErrorCode(err: unknown): string {
  const httpErr = err as HttpErrorResponse;
  return (httpErr?.error as Record<string, unknown>)?.['code'] as string ?? '';
}

function readHttpStatus(err: unknown): number {
  return (err as HttpErrorResponse)?.status ?? 0;
}

@Injectable({ providedIn: 'root' })
export class IapService {
  private readonly userService = inject(UserService);
  private readonly subscriptionService = inject(SubscriptionService);

  readonly isNativeIos = Capacitor.getPlatform() === 'ios';

  /** Prod: iOS only. Dev: all devices/platforms for UI testing. */
  readonly showButton = this.isNativeIos || !environment.production;

  /** Restore button: iOS in prod, all platforms in dev. */
  readonly showRestoreButton = this.isNativeIos || !environment.production;

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  async getMonthlyPrice(): Promise<string | null> {
    log('getMonthlyPrice — plataforma:', Capacitor.getPlatform(), '| productId:', environment.iapProductId);
    if (!this.isNativeIos || !environment.iapProductId) {
      log('getMonthlyPrice — não é iOS nativo ou productId vazio, retornando null');
      return null;
    }
    try {
      const { price } = await LumioIAP.getMonthlyPrice({ productId: environment.iapProductId });
      log('getMonthlyPrice — preço recebido do StoreKit:', price);
      return price;
    } catch (err) {
      log('getMonthlyPrice — erro ao buscar preço:', err);
      return null;
    }
  }

  async purchaseMonthly(): Promise<IapPurchaseResult> {
    log('purchaseMonthly — início. isNativeIos:', this.isNativeIos, '| production:', environment.production);

    if (!this.isNativeIos) {
      if (environment.production) {
        log('purchaseMonthly — não é iOS em prod, cancelando silenciosamente');
        return 'cancelled';
      }
      log('purchaseMonthly — modo DEV: exibindo prompt para transactionId manual');
      const raw = window.prompt('[DEV] Apple sandbox transaction ID para /verify (cancelar = abortar):');
      if (!raw?.trim()) {
        log('purchaseMonthly — prompt cancelado pelo usuário');
        return 'cancelled';
      }
      const txnId = raw.trim();
      log('purchaseMonthly — DEV: transactionId informado:', txnId);
      await Preferences.set({ key: PENDING_TXN_KEY, value: txnId });
      return await this.verifyAndFinish(txnId);
    }

    if (!environment.iapProductId) {
      log('purchaseMonthly — iapProductId vazio no environment, cancelando');
      return 'cancelled';
    }

    try {
      log('purchaseMonthly — buscando dados do usuário (getMe)...');
      const user = await firstValueFrom(this.userService.getMe());
      log('purchaseMonthly — userId (appAccountToken):', user.id);
      log('purchaseMonthly — chamando LumioIAP.purchaseMonthly com productId:', environment.iapProductId);

      const { transactionId } = await LumioIAP.purchaseMonthly({
        productId: environment.iapProductId,
        appAccountToken: user.id,
      });

      log('purchaseMonthly — StoreKit retornou transactionId:', transactionId);
      await Preferences.set({ key: PENDING_TXN_KEY, value: transactionId });
      log('purchaseMonthly — transactionId persistido, chamando verifyAndFinish...');

      return await this.verifyAndFinish(transactionId);
    } catch (err: unknown) {
      const code = (err as { code?: string }).code ?? '';
      log('purchaseMonthly — ERRO capturado. code:', code, '| err:', err);
      if (code === 'USER_CANCELLED') return 'cancelled';
      if (code === 'PENDING') return 'pending';
      return 'error';
    }
  }

  async restorePurchases(): Promise<IapRestoreResult> {
    log('restorePurchases — início. isNativeIos:', this.isNativeIos);

    if (!this.isNativeIos && environment.production) {
      log('restorePurchases — não é iOS em prod, retornando error');
      return 'error';
    }

    if (this.isNativeIos) {
      log('restorePurchases — chamando LumioIAP.restorePurchases (AppStore.sync)...');
      try {
        await LumioIAP.restorePurchases();
        log('restorePurchases — AppStore.sync concluído');
      } catch (err) {
        log('restorePurchases — AppStore.sync falhou (não bloqueante):', err);
      }
    }

    log('restorePurchases — chamando backend /billing/apple/restore...');
    try {
      const sub = await firstValueFrom(this.subscriptionService.restoreIapPurchase());
      const result: IapRestoreResult = sub !== null ? 'success' : 'not_found';
      log('restorePurchases — resposta do backend:', sub, '→ resultado:', result);
      return result;
    } catch (err: unknown) {
      const status = readHttpStatus(err);
      const code = readErrorCode(err);
      log('restorePurchases — ERRO do backend. status:', status, '| code:', code, '| err:', err);
      if (status === 404) return 'not_found';
      if (status === 503) return 'unavailable';
      if (code === 'BILLING_ALREADY_SUBSCRIBED_VIA_OTHER_PROVIDER') return 'wrong_provider';
      return 'error';
    }
  }

  /**
   * Called on iOS app launch and after login.
   * On iOS: (a) drains pending /verify retries, (b) re-syncs entitlement via /restore.
   * On dev/web: skips StoreKit drain, goes straight to silent /restore.
   */
  async drainAndRestore(): Promise<void> {
    log('drainAndRestore — início. isNativeIos:', this.isNativeIos, '| production:', environment.production);

    if (!this.isNativeIos && environment.production) {
      log('drainAndRestore — não é iOS em prod, encerrando');
      return;
    }

    if (this.isNativeIos) {
      const pendingIds = new Set<string>();

      const { value: storedId } = await Preferences.get({ key: PENDING_TXN_KEY });
      if (storedId) {
        log('drainAndRestore — transactionId pendente encontrado no Preferences:', storedId);
        pendingIds.add(storedId);
      }

      try {
        const { transactionIds } = await LumioIAP.getPendingTransactions();
        log('drainAndRestore — transações pendentes no StoreKit:', transactionIds);
        transactionIds.forEach((id) => pendingIds.add(id));
      } catch (err) {
        log('drainAndRestore — getPendingTransactions falhou (iOS < 15?):', err);
      }

      if (pendingIds.size === 0) {
        log('drainAndRestore — nenhuma transação pendente para drenar');
      } else {
        log('drainAndRestore — drenando', pendingIds.size, 'transação(ões):', [...pendingIds]);
        for (const transactionId of pendingIds) {
          log('drainAndRestore — verificando transação pendente:', transactionId);
          await Preferences.set({ key: PENDING_TXN_KEY, value: transactionId });
          await this.verifyAndFinish(transactionId);
        }
      }

      log('drainAndRestore — chamando AppStore.sync antes do /restore...');
      await LumioIAP.restorePurchases().catch((err) => {
        log('drainAndRestore — AppStore.sync falhou (não bloqueante):', err);
      });
    }

    log('drainAndRestore — chamando /billing/apple/restore silenciosamente...');
    try {
      const sub = await firstValueFrom(this.subscriptionService.restoreIapPurchase());
      log('drainAndRestore — /restore respondeu:', sub);
    } catch (err: unknown) {
      const status = readHttpStatus(err);
      log('drainAndRestore — /restore falhou silenciosamente. status:', status, '(404=sem compra prévia, 503=Apple down)');
    }
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  private async verifyAndFinish(transactionId: string): Promise<IapPurchaseResult> {
    log('verifyAndFinish — chamando POST /billing/apple/verify. transactionId:', transactionId);
    try {
      const sub = await firstValueFrom(this.subscriptionService.notifyIapPurchase(transactionId));
      log('verifyAndFinish — /verify SUCESSO. subscription:', sub);

      if (this.isNativeIos) {
        log('verifyAndFinish — chamando LumioIAP.finishTransaction para encerrar no StoreKit...');
        await LumioIAP.finishTransaction({ transactionId });
        log('verifyAndFinish — finishTransaction concluído');
      }
      await Preferences.remove({ key: PENDING_TXN_KEY });
      log('verifyAndFinish — pendingTxnKey removido do Preferences. Resultado: success');
      return 'success';
    } catch (err: unknown) {
      const status = readHttpStatus(err);
      const code = readErrorCode(err);
      log('verifyAndFinish — /verify FALHOU. status:', status, '| code:', code, '| err:', err);

      if (status === 503) {
        log('verifyAndFinish — 503 transiente, mantendo transação pendente para retry no próximo launch');
        return 'service_unavailable';
      }

      if (code === 'BILLING_ALREADY_SUBSCRIBED_VIA_OTHER_PROVIDER') return 'already_subscribed_stripe';
      if (code === 'BILLING_SUBSCRIPTION_ALREADY_EXISTS') return 'already_subscribed_apple';
      if (code === 'BILLING_APPLE_TXN_LINKED_TO_ANOTHER_ACCOUNT') return 'linked_to_other_account';

      log('verifyAndFinish — erro não-retentável, finalizando transação no StoreKit e limpando Preferences');
      if (this.isNativeIos) {
        await LumioIAP.finishTransaction({ transactionId }).catch((e) =>
          log('verifyAndFinish — finishTransaction (cleanup) falhou:', e)
        );
      }
      await Preferences.remove({ key: PENDING_TXN_KEY });

      if (code === 'BILLING_APPLE_SUBSCRIPTION_NOT_ACTIVE') return 'subscription_not_active';
      return 'error';
    }
  }
}
